import React from 'react';

/**
 * Resolves Shopify GIDs to actual URLs using the Storefront API
 */

// Cache to avoid repeated API calls for the same GID
const gidUrlCache = new Map<string, string | null>();

/**
 * Resolves a Shopify MediaImage GID to its CDN URL
 */
export async function resolveMediaGidToUrl(gid: string, storefrontAccessToken?: string): Promise<string | null> {
  if (!gid || !gid.includes('MediaImage')) {
    return null;
  }

  // Check cache first
  if (gidUrlCache.has(gid)) {
    return gidUrlCache.get(gid) || null;
  }

  // Get storefront access token - this is safe to expose publicly
  const accessToken = storefrontAccessToken || 
    (typeof window !== 'undefined' ? (window as any).SHOPIFY_STOREFRONT_ACCESS_TOKEN : undefined) ||
    'a74570e59dc797a990a1317bc4ff8cab'; // Safe to hardcode this
  
  if (!accessToken) {
    console.warn('No Shopify Storefront access token available');
    gidUrlCache.set(gid, null);
    return null;
  }

  const shopDomain = typeof window !== 'undefined' 
    ? window.location.hostname 
    : 'space-furniture.myshopify.com';

  const query = `
    query {
      node(id: "${gid}") {
        id
        __typename
        ... on MediaImage {
          image {
            url
            altText
          }
        }
      }
    }
  `;

  try {
    const response = await fetch(`https://${shopDomain}/api/2025-04/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': accessToken,
      },
      body: JSON.stringify({
        query
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: any = await response.json();
    
    if (data.data?.node?.image?.url) {
      const imageUrl = data.data.node.image.url;
      gidUrlCache.set(gid, imageUrl);
      return imageUrl;
    }

    gidUrlCache.set(gid, null);
    return null;

  } catch (error) {
    console.error('Error resolving Shopify GID:', error);
    gidUrlCache.set(gid, null);
    return null;
  }
}

/**
 * Hook for React components to resolve GIDs
 */
export function useResolvedImage(gidOrUrl: string | null | undefined): string | null {
  const [resolvedUrl, setResolvedUrl] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!gidOrUrl) {
      setResolvedUrl(null);
      return;
    }

    // If it's already a URL, use it directly
    if (gidOrUrl.startsWith('http')) {
      setResolvedUrl(gidOrUrl);
      return;
    }

    // If it's a GID, resolve it
    if (gidOrUrl.includes('MediaImage')) {
      resolveMediaGidToUrl(gidOrUrl)
        .then(setResolvedUrl)
        .catch(() => setResolvedUrl(null));
    } else {
      setResolvedUrl(null);
    }
  }, [gidOrUrl]);

  return resolvedUrl;
}
